<template>
  <div class="performance-period-summary-view">
    <v-container
      v-if="eventDetails.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="100"
      />
    </v-container>
    <div v-else>
      <div>
        <v-row
          align="center"
          class="mt-0"
        >
          <v-col cols="auto">
            <v-btn
              icon
              @click="$router.push({name: $routeNames.PERFORMANCE_EVENT, query: {event_id: eventDetails.data.id}})"
            >
              <v-icon small>
                fa-light fa-chevron-left
              </v-icon>
            </v-btn>
          </v-col>
          <v-col>
            <span class="text-h6 font-weight-medium text-none primary--text">
              {{ eventDetails.data.title }}
            </span>
            <v-row
              align="center"
              no-gutters
            >
              <v-col cols="auto">
                {{ dayjs(eventDetails.data.start_at).format('dddd, MMMM D HH:mm') }} - {{ dayjs(eventDetails.data.end_at).format('HH:mm') }}
              </v-col>
              <v-col
                v-if="eventDetails.data.event_type"
                cols="auto"
                class="pl-3"
              >
                <v-chip
                  label
                  :color="eventTypes[eventDetails.data.event_type].color"
                  dark
                  small
                >
                  {{ eventTypes[eventDetails.data.event_type].text }}
                </v-chip>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="auto">
            <layout-selector
              :selected-layout.sync="selectedLayout"
              :layouts.sync="layouts"
              @add-layout="openAddLayoutDialog"
              @edit-layout="openEditLayoutDialog"
            />
          </v-col>
        </v-row>
        <v-divider class="mt-3" />
      </div>

      <grid-layout
        v-if="selectedLayout"
        :key="selectedLayout.name"
        :layout="adjustedGridLayout"
        :col-num="12"
        :row-height="70"
        :is-draggable="false"
        :is-resizable="false"
        :vertical-compact="true"
        :margin="[10, 10]"
        :use-css-transforms="true"
        @layout-ready="layoutReady = true"
      >
        <grid-item
          v-for="item in adjustedGridLayout"
          :key="item.i"
          :x="item.x"
          :y="item.y"
          :w="item.w"
          :h="item.h"
          :i="item.i"
        >
          <div
            v-if="layoutReady"
            class="fill-height"
          >
            <div
              v-if="item.type === 'table'"
              class="fill-height"
            >
              <v-card class="fill-height rounded-lg">
                <v-card-title class="px-6 py-4">
                  <v-row
                    no-gutters
                    align="center"
                  >
                    <v-col>
                      <div class="subtitle-2 grey--text text--grey-100">
                        {{ $t('card_title.period_summary') }}
                      </div>
                    </v-col>
                    <v-col
                      cols="auto"
                      class="pl-2"
                    >
                      <variable-selector
                        v-if="selectedLayout && variables.data"
                        :variables="variables.data"
                        :layout-variables.sync="tableHeaders"
                        @update="saveLayoutVariables"
                      />
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-card-text class="pa-0">
                  <performance-period-table
                    class="pb-2"
                    :table-headers="tableHeaders"
                    :periods="periods.data"
                    :loading="periods.loading"
                    :max-values="periods.maxValues"
                  />
                </v-card-text>
              </v-card>
            </div>

            <div
              v-else-if="item.type === 'card'"
              class="fill-height"
            >
              <stat-card
                class="fill-height"
                :loading="periods.loading"
                :title="getVariableName(item.key)"
                :value="teamAverage(item.key)"
                :max="periods.maxValues[item.key]"
                :color="getColorFromValue(teamAverage(item.key), periods.maxValues[item.key], reversedVariables().includes(item.key))"
              />
            </div>

            <div
              v-else-if="item.type === 'chart'"
              class="fill-height"
            >
              <performance-period-chart
                class="fill-height"
                :periods="periods.data"
                :chart="item"
              />
            </div>
          </div>
        </grid-item>
      </grid-layout>
    </div>
  </div>
</template>

<script>
import { EventApiService } from '@/data/services'
import PerformancePeriodTable from '@/components/performance/PerformancePeriodTable.vue'
import { adjustGridForTableHeights, getColorForValue, processMaxValues } from '@/utils/helpers'
import { DashboardType } from '@/data/models/Dashboard'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { EventBus } from '@/plugins/bus'
import { LayoutApiService } from '@/data/services/LayoutApiService'
import { GridItem, GridLayout } from 'vue-grid-layout'
import StatCard from '@/components/StatCard'
import { EventType, EventTypes } from '@/data/models'
import { reversedVariables } from '@/data/models/TableHeader'
import { orderBy, debounce } from 'lodash'
import { VariableApiService } from '@/data/services/VariableApiService'
import dayjs from 'dayjs'
import PerformancePeriodChart from '@/components/charts/implementation/PerformancePeriodChart.vue'
import LayoutSelector from '@/components/LayoutSelector.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'

export default {
  name: 'PerformancePeriodSummaryView',
  components: {
    LayoutSelector,
    PerformancePeriodChart,
    VariableSelector,
    PerformancePeriodTable,
    GridLayout,
    GridItem,
    StatCard
  },
  data() {
    return {
      eventDetails: {
        data: {},
        loading: false
      },
      layoutReady: false,
      periods: {
        data: [],
        loading: false,
        maxValues: {}
      },
      variables: {
        data: [],
        loading: false
      },
      selectedLayoutObject: null,
      selectedVariablesObject: null,
      dashboard: DashboardType.PERIOD_SUMMARY,
      submitting: false,
      eventTypeMatch: EventType.MATCH,
      eventTypeTraining: EventType.TRAINING,
      eventTypes: EventTypes,
      events: {
        loading: false,
        data: [],
        pagination: {
          per_page: 10,
          current_page: 1,
          last_page: null
        }
      },
      filters: {
        eventType: undefined,
        dateFrom: dayjs().subtract(3, 'month').format('YYYY-MM-DD'),
        dateTo: dayjs().format('YYYY-MM-DD'),
        eventName: ''
      },
      filterDateFromActive: false,
      filterDateToActive: false
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.PERIOD_SUMMARY)
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return this.selectedLayoutObject
        }
        return this.layouts[0]
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    layoutVariables: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    },
    adjustedGridLayout() {
      if (!this.selectedLayout || !this.selectedLayout.grid) return []

      return adjustGridForTableHeights(this.selectedLayout.grid, this.periods?.data?.length || 0, 0)
    },
    tableHeaders() {
      return this.selectedLayout ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.layoutVariables, ['order'])) : []
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    }
  },
  watch: {
    selectedLayout() {
      this.selectedVariablesObject = null
    }
  },
  created() {
    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })

    EventBus.$on(this.$events.EVENT_UPDATED, (event) => {
      if (this.eventDetails.data && this.eventDetails.data.id === event.id) {
        this.fetchEventDetails()
      }
    })

    EventBus.$on(this.$events.EVENT_DELETED, () => {
      this.$router.push({ name: 'Events' })
    })

    this.fetchVariables()
    this.fetchEventDetails()
    this.fetchAllPeriods()
  },
  beforeDestroy() {
    EventBus.$off(this.$events.LAYOUT_UPDATED)
    EventBus.$off(this.$events.EVENT_UPDATED)
    EventBus.$off(this.$events.EVENT_DELETED)
  },
  methods: {
    dayjs,
    orderBy,
    reversedVariables() {
      return reversedVariables
    },
    async fetchEventDetails() {
      this.eventDetails.loading = true

      try {
        const response = await EventApiService.getEventDetails(this.$route.params.id)

        this.eventDetails.data = response.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_event.error'),
          color: 'error'
        })
      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchAllPeriods() {
      this.periods.loading = true

      try {
        const response = await EventApiService.getEventPeriods(this.$route.params.id)
        this.periods.data = response.data

        this.periods.maxValues = processMaxValues(this.periods.data.map(period => period.teams[0].team_stats), 1.1)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_periods.error'),
          color: 'error'
        })
      } finally {
        this.periods.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.PERIOD_SUMMARY].variable_categories
        })

        this.variables.data = result.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })
      } finally {
        this.variables.loading = false
      }
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: DashboardType.PERIOD_SUMMARY
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: DashboardType.PERIOD_SUMMARY,
        selectedLayout: this.selectedLayout
      })
    },
    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.text
    },
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    teamAverage(key) {
      if (!this.periods.data || this.periods.data.length === 0) return 0

      const validValues = this.periods.data
        .map(period => period.teams[0]?.team_stats?.[key])
        .filter(value => value !== undefined && value !== null)

      if (validValues.length === 0) return 0

      return Math.round(validValues.reduce((sum, value) => sum + value, 0) / validValues.length)
    },
    getChartTitle(chart) {
      if (chart.title) return chart.title

      const variables = Array.isArray(chart.key) ? chart.key : [chart.key]

      return variables
        .map(key => this.getVariableName(key))
        .join(' vs ')
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.layoutVariables.map(v => ({
            id: v.id,
            order: v.order
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000)
  }
}
</script>
